import React from 'react';
import { ButtonBase } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledCustomButton = styled(ButtonBase, {
    shouldForwardProp: (prop) => !['customvariant', 'customcolor', 'fullWidth', 'size'].includes(prop),
})(({ theme, customvariant, customcolor, fullWidth, size, padding, fontSize, height, minWidth }) => {

    const getColorPalette = (color) => {
        switch (color) {
            case 'secondary':
                return theme.palette.secondary;
            case 'error':
                return theme.palette.error;
            case 'warning':
                return theme.palette.warning;
            case 'info':
                return theme.palette.info;
            case 'success':
                return theme.palette.success;
            case 'disabled':
                return {
                    main: theme.palette.grey[300],
                    dark: theme.palette.grey[400],
                    contrastText: theme.palette.grey[500]
                };
            case 'default':
                return {
                    main: 'transparent',
                    dark: '#666',
                    contrastText: '#666'
                };
            default:
                return theme.palette.primary;
        }
    };

    const colorPalette = getColorPalette(customcolor);

    // Handle size variations
    const getSizeStyles = (size) => {
        switch (size) {
            case 'small':
                return {
                    padding: "8px 16px",
                    fontSize: "12px",
                    height: "32px",
                    minWidth: "120px",
                };
            case 'large':
                return {
                    padding: "16px 32px",
                    fontSize: "16px",
                    height: "56px",
                    minWidth: "280px",
                };
            case 'custom':
                return {
                    padding: padding || "inherit",
                    fontSize: fontSize || "14px",
                    height: height || "auto",
                    minWidth: minWidth || "auto",
                };
            default: // medium
                return {
                    padding: "14px 28px",
                    fontSize: "14px",
                    height: "48px",
                    minWidth: "220px",
                };
        }
    };

    const sizeStyles = getSizeStyles(size);

    const baseStyles = {
        margin: "3px 0",
        ...sizeStyles,
        borderRadius: "12px",
        fontWeight: 600,
        textTransform: "none",
        letterSpacing: "0.5px",
        width: fullWidth ? "100%" : "auto",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s ease, box-shadow 0.3s ease, background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease",
        position: "relative",
        overflow: "hidden",
        "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: "-100%",
            width: "100%",
            height: "100%",
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
            opacity: 0,
            pointerEvents: "none",
        },
        "&:hover::before": {
            opacity: 1,
            animation: "shimmerIn 0.5s ease forwards",
        },
        "&:not(:hover)::before": {
            animation: "shimmerOut 0.5s ease forwards",
        },
        "@keyframes shimmerIn": {
            "0%": {
                left: "-100%",
                opacity: 0,
            },
            "100%": {
                left: "100%",
                opacity: 1,
            },
        },
        "@keyframes shimmerOut": {
            "0%": {
                left: "100%",
                opacity: 1,
            },
            "100%": {
                left: "-100%",
                opacity: 0,
            },
        },
        "&:active": {
            transform: "translateY(-1px) scale(0.98)",
            transition: "all 0.1s ease",
        },
        "&:focus": {
            outline: "none",
        },
    };

    // Gradient variant (custom)
    if (customvariant === 'gradient') {
        return {
            ...baseStyles,
            background: `linear-gradient(to left, #041e42, #78a8e6)`,
            color: "#ffffff",
            border: "none",
            boxShadow: `0 4px 16px ${colorPalette.main}30`,
            "&:hover": {
                background: `linear-gradient(to left, #041e42, #78a8e6)`,
                boxShadow: `0 8px 25px ${colorPalette.main}50`,
                transform: "translateY(-3px) scale(1.02)",
            },
            "&:focus": {
                ...baseStyles["&:focus"],
                boxShadow: `0 0 0 3px ${colorPalette.main}20`,
            },
            // Ensure shimmer works for gradient variant
            "&:hover::before": {
                opacity: 1,
                animation: "shimmerIn 0.5s ease forwards",
            },
            "&:not(:hover)::before": {
                animation: "shimmerOut 0.5s ease forwards",
            },
        };
    }

    // Contained variant
    if (customvariant === 'contained') {
        return {
            ...baseStyles,
            backgroundColor: colorPalette.main === 'transparent' ? 'transparent' : colorPalette.main,
            color: colorPalette.contrastText || "#ffffff",
            border: colorPalette.main === 'transparent' ? `1px solid ${colorPalette.dark}` : 'none',
            boxShadow: colorPalette.main === 'transparent' ? 'none' : `0 4px 16px ${colorPalette.main}30`,
            "&:hover": {
                backgroundColor: colorPalette.main === 'transparent' ? `${colorPalette.dark}20` : colorPalette.dark,
                borderColor: colorPalette.main === 'transparent' ? colorPalette.dark : 'inherit',
                boxShadow: colorPalette.main === 'transparent' ? `0 2px 8px ${colorPalette.dark}30` : `0 8px 25px ${colorPalette.main}50`,
                transform: "translateY(-3px) scale(1.02)",
            },
            "&:focus": {
                ...baseStyles["&:focus"],
                boxShadow: `0 0 0 3px ${colorPalette.dark}20`,
            },
        };
    }

    // Outlined variant
    if (customvariant === 'outlined') {
        return {
            ...baseStyles,
            backgroundColor: colorPalette.main === 'transparent' ? "transparent" : colorPalette.main,
            color: colorPalette.contrastText,
            border: `2px solid ${colorPalette.contrastText}`,
            boxShadow: "none",
            "&:hover": {
                backgroundColor: colorPalette.main === 'transparent' ? `${colorPalette.dark}10` : colorPalette.dark,
                borderColor: colorPalette.contrastText,
                color: colorPalette.contrastText,
                transform: "translateY(-3px) scale(1.02)",
                boxShadow: `0 6px 20px ${colorPalette.contrastText}30`,
            },
            "&:focus": {
                ...baseStyles["&:focus"],
                boxShadow: `0 0 0 3px ${colorPalette.contrastText}20`,
            },
        };
    }

    // Text variant
    if (customvariant === 'text') {
        return {
            ...baseStyles,
            backgroundColor: "transparent",
            color: colorPalette.main,
            border: "none",
            boxShadow: "none",
            minWidth: "auto",
            padding: "8px 16px",
            "&:hover": {
                backgroundColor: `${colorPalette.main}10`,
                color: colorPalette.dark,
                transform: "translateY(-2px) scale(1.02)",
                boxShadow: `0 4px 12px ${colorPalette.main}20`,
            },
            "&:focus": {
                ...baseStyles["&:focus"],
                boxShadow: `0 0 0 3px ${colorPalette.main}20`,
            },
        };
    }

    // Default to contained if no variant specified
    return {
        ...baseStyles,
        backgroundColor: colorPalette.main,
        color: colorPalette.contrastText || "#ffffff",
        boxShadow: `0 4px 16px ${colorPalette.main}30`,
        "&:hover": {
            backgroundColor: colorPalette.dark,
            boxShadow: `0 8px 25px ${colorPalette.main}50`,
            transform: "translateY(-3px) scale(1.02)",
        },
        "&:focus": {
            ...baseStyles["&:focus"],
            boxShadow: `0 0 0 3px ${colorPalette.main}20`,
        },
    };
});

const CustomButton = ({
    children,
    startIcon = null,
    endIcon = null,
    onClick,
    disabled = false,
    fullWidth = false,
    size = "medium",
    variant = "outlined",
    color = "default",
    ...props
}) => {
    // Override variant and color when disabled
    const finalVariant = disabled ? "outlined" : variant;
    const finalColor = disabled ? "disabled" : color;

    return (
        <StyledCustomButton
            onClick={onClick}
            disabled={disabled}
            fullWidth={fullWidth}
            size={size}
            customvariant={finalVariant}
            customcolor={finalColor}
            {...props}
        >
            {startIcon ?
                <span style={{ marginRight: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    {startIcon}
                    {children}
                </span>
                : endIcon ?
                    <span style={{ marginLeft: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        {children}
                        {endIcon}
                    </span>
                    : children
            }
        </StyledCustomButton>
    );
};

export default CustomButton;